import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

const DiscoverPage = () => {
  const navigate = useNavigate();

  // Ensure the body background is dark
  useEffect(() => {
    const originalBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#1a1a1a';
    return () => {
      document.body.style.backgroundColor = originalBg;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#1a1a1a] text-white w-full" style={{ fontFamily: 'system-ui' }}>
      {/* Fixed Header/Navbar */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#1a1a1a] border-b border-gray-800 w-full">
        <div className="flex justify-between items-center px-8 py-6">
          <div className="flex items-center space-x-2">
            <img 
              src="/discover_page_arta_logo.png"
              alt="Arta Logo" 
              className="h-16 w-auto"
            />
            <span className="text-xs text-gray-400">stealth</span>
          </div>
          <button
            onClick={() => navigate('/login')}
            className="bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            Click here for Demo
          </button>
        </div>
      </header>

      {/* Main Content with top padding to account for fixed header */}
      <div className="pt-32 w-full">
        {/* Hero Section */}
        <section className="text-center py-20 w-full">
          <h1 className="text-5xl font-bold mb-6 max-w-4xl mx-auto">
            Enabling Enterprises to Improve Customer Loyalty and Support Cost
          </h1>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Transform technical support with AI Pilots and co-pilots delivering consistently exceptional customer experiences and empowering support teams to achieve 50% greater productivity.
          </p>
        </section>

        {/* Testimonials Section */}
        <section className="py-20 w-full">
          <h2 className="text-4xl font-bold text-center mb-16">What support leaders are saying</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto px-8">
            <div className="bg-white text-black p-8 rounded-lg shadow-lg">
              <p className="text-lg leading-relaxed mb-6">
                In today's fast-evolving landscape, support functions face relentless pressure to boost productivity while cutting costs. With 70% of support expenses tied to personnel—and salaries rising even in offshore operations—traditional approaches fall short. Embracing AI-driven automation is no longer optional; it's the key to unlocking unprecedented efficiency and transforming customer success.
              </p>
              <div className="font-bold">Chief Customer Officer</div>
              <div className="text-gray-600">Fortune 500 Company</div>
            </div>
            
            <div className="bg-white text-black p-8 rounded-lg shadow-lg">
              <p className="text-lg leading-relaxed mb-6">
                Customer loyalty fuels our organic growth, but scaling support to deliver exceptional experiences is a formidable challenge. Training teams and managing costs while meeting rising demands requires a bold approach. AI-powered solutions are our catalyst for sustainable, cost-effective scaling that delights customers and drives success.
              </p>
              <div className="font-bold">Vice President of Customer Success</div>
              <div className="text-gray-600">Fast-Growing Mid-Size Company</div>
            </div>
            
            <div className="bg-white text-black p-8 rounded-lg shadow-lg">
              <p className="text-lg leading-relaxed mb-6">
                Our startup's rapid growth demands 24/7 support, but hiring and training engineers fast enough while ensuring quality—especially with offshore resources—has been a steep challenge. AI-powered support empowers our core team of senior engineers to scale instantly, delivering exceptional service from day one.
              </p>
              <div className="font-bold">Vice President of Support</div>
              <div className="text-gray-600">Fast-Growing Startup Company</div>
            </div>
          </div>
        </section>

        {/* Comprehensive Solutions Section */}
        <section className="py-20 w-full">
          <h2 className="text-4xl font-bold text-center mb-16">ARTA'S COMPREHENSIVE SOLUTIONS</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto px-8">
            {/* Row 1 */}
            <div className="bg-purple-300 p-8 rounded-lg">
              <div className="w-16 h-16 mx-auto mb-4">
                <img 
                  src="/copilot.png"
                  alt="Co-pilot Icon" 
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-purple-900">Support Auto Resolution & Co-Pilot</h3>
              <ul className="space-y-2 text-sm text-purple-800">
                <li>• Understand problem, requests data from customer or gets feed from real-time data sources available</li>
                <li>• Classifies the problem, and computes the plan of action</li>
                <li>• Resolves the support request or engages the right-expert to join live</li>
              </ul>
            </div>
            
            <div className="bg-green-300 p-8 rounded-lg">
              <div className="w-16 h-16 mx-auto mb-4">
                <img 
                  src="/user.png"
                  alt="User Icon" 
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-green-900">Customer Success Agent</h3>
              <ul className="space-y-2 text-sm text-green-800">
                <li>• Sentiment analysis on customer interaction with support Co-pilot and/or live engineer</li>
                <li>• Proactive notification / escalation of critical cases or customer sat issues to support leaders</li>
                <li>• Customer Sat Scoring based on the entire interaction with customer</li>
              </ul>
            </div>
            
            <div className="bg-orange-300 p-8 rounded-lg">
              <div className="w-16 h-16 mx-auto mb-4">
                <img 
                  src="/lightbulb.png"
                  alt="Lightbulb Icon" 
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-orange-900">Solution Co-pilot</h3>
              <ul className="space-y-2 text-sm text-orange-800">
                <li>• Answers customers' questions related to design, configuration, optimization, or security issues</li>
                <li>• Monitors operational data points that are early indicators of errors within syslogs, release notes, security vulnerabilities</li>
                <li>• Predicts and proactively notifies solution engineers of potential problems</li>
              </ul>
            </div>
            
            {/* Row 2 */}
            <div className="bg-gray-100 text-black p-8 rounded-lg">
              <div className="w-16 h-16 mx-auto mb-4">
                <img 
                  src="/degreecap.png"
                  alt="Degree Cap Icon" 
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-2xl font-bold mb-4">Instant Learning</h3>
              <ul className="space-y-2 text-sm">
                <li>• Automatically learns from every update to the data sources and every customer interaction</li>
                <li>• As a copilot continuously guides the support engineer with available debugging options</li>
              </ul>
            </div>
            
            <div className="bg-gray-100 text-black p-8 rounded-lg">
              <div className="w-16 h-16 mx-auto mb-4">
                <img 
                  src="/analysis.png"
                  alt="Analysis Icon" 
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-2xl font-bold mb-4">Analytics & Insights</h3>
              <p className="text-sm">
                provides analytics on case resolution metrics by case type, assigned engineers, etc.
              </p>
            </div>
            
            <div className="bg-gray-100 text-black p-8 rounded-lg">
              <div className="w-16 h-16 mx-auto mb-4">
                <img 
                  src="/settings.png"
                  alt="Settings Icon" 
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-2xl font-bold mb-4">Seamless Integrations</h3>
              <ul className="space-y-2 text-sm">
                <li>• Easy integration with all the data sources that trains our diagnostics engine</li>
                <li>• Easily integrates with customers Web portal for consistent branding or preferred tools</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Value Proposition Section */}
        <section className="py-20 w-full">
          <h2 className="text-4xl font-bold text-center mb-16">Arta's Value Proposition</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto px-8">
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-4">Support Cost Reduction</h3>
              <p className="text-gray-300 leading-relaxed">
                Arta's state-of-the-art Diagnostics engine auto-resolves up to 50% of customer support cases and boosts engineer productivity by 50% when escalation is needed, driving unparalleled efficiency and customer satisfaction.
              </p>
            </div>
            
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-4">Customer Success</h3>
              <p className="text-gray-300 leading-relaxed">
                Arta's Support Diagnostics engine raises the floor of customer experience with proven AI-driven diagnostics, pinpointing issues to slash time-to-resolution and deliver consistent, reliable support at scale.
              </p>
            </div>
            
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-4">Premium Services Revenue</h3>
              <p className="text-gray-300 leading-relaxed">
                Arta's real-time monitoring of application behavior and its revolutionary AI engine enables enterprise vendors to offer premium add-on services.
              </p>
            </div>
          </div>
        </section>

        {/* Founders Section */}
        <section className="py-20 w-full">
          <h2 className="text-4xl font-bold text-center mb-16">Meet Our Founders</h2>
          <div className="grid md:grid-cols-2 gap-16 max-w-5xl mx-auto px-8">
            <div className="text-center">
              <div className="w-48 h-48 mx-auto mb-6 rounded-full overflow-hidden">
                <img 
                  src="/Burjiz.jpg"
                  alt="Burjiz Pithawala" 
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-2xl font-bold mb-2">Burjiz Pithawala</h3>
              <p className="text-gray-400 mb-4">Co-Founder & CEO</p>
              <p className="text-gray-300 leading-relaxed text-sm">
                Burjiz Pithawala leads Arta's Product Strategy. He pioneered the development of one of the tech industry's first Support Automation Platforms at Cisco Systems—a groundbreaking innovation that not only greatly accelerated time-to-resolution for customer support issues but also laid the foundation for delivering proactive services to customers. Burjiz has held multiple senior executive and General Manager roles at leading companies including Cisco and Arrcus, and he was the founder and CEO of Elisity, a successful security platform company. He holds a Master's degree in Computer Science from [University Name].
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-48 h-48 mx-auto mb-6 rounded-full overflow-hidden">
                <img 
                  src="/Brad.jpg"
                  alt="Brad Kashani" 
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-2xl font-bold mb-2">Brad Kashani</h3>
              <p className="text-gray-400 mb-4">Co-Founder & CBO</p>
              <p className="text-gray-300 leading-relaxed text-sm">
                Brad Kashani leads Arta's Customer and Business Operations. With over 20 years of executive leadership, Brad has built world-class support and consulting organizations recognized with numerous industry awards at companies such as Cisco and Juniper Networks. He has been a trailblazer in the services sector, pioneering Cisco's Advanced Services—a multi-billion dollar business today—and spearheading the development of Support Automation platforms that redefined customer experience at Cisco, Procket, and Juniper Networks. Brad holds a Master's degree in Computer Science from the University of Colorado and an MBA from UCLA.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default DiscoverPage; 